import * as React from 'react';

import { Button, Checkbox, Popover, Text } from '@/components/ui';

// DataGridToolbar — renders a horizontal row of action buttons above
// the table, plus a right-aligned column-visibility control.
//
// Layout: actions on the left (New / Edit / Delete-style affordances
// that mutate data), Columns popover on the right (table-chrome that
// mutates the view, not the data). The split is intentional — pairing
// them on the same side would read as if "Columns" were just another
// action on the dataset.
//
// Each action declares a `requires` constraint that the toolbar
// resolves against the current selection count to compute `disabled`
// automatically:
//
//   requires: 'none'      → always enabled                    (default)
//   requires: 'single'    → enabled when exactly 1 row picked
//   requires: 'any'       → enabled when ≥1 rows picked
//   requires: 'multiple'  → enabled when ≥2 rows picked
//
// The toolbar layout follows pattern (a) from the design discussion:
// the action set never collapses into a "selection bar" — instead each
// action is enabled/disabled in place, so the row of affordances stays
// stable while the user selects.

const isActionEnabled = (action, selectedCount) => {
    const requires = action.requires ?? 'none';
    switch (requires) {
        case 'single':
            return selectedCount === 1;
        case 'any':
            return selectedCount >= 1;
        case 'multiple':
            return selectedCount >= 2;
        case 'none':
        default:
            return true;
    }
};

// Popover body: heading + checkbox list. Each checkbox toggles a column
// via the parent's `onColumnVisibilityChange`. A checked, currently-only-
// at-the-floor row is disabled so the user can't drop below two visible
// columns; hidden rows remain clickable so the user can always bring
// columns back. The selection column is *not* listed — it's chrome.
//
// First-letter:uppercase on the column label mirrors how DataGrid
// renders header labels: the JSON value is left as-authored (e.g.
// `i18next.t('common.name')` → "name") and the cap is purely a CSS
// concern.
const ColumnsPopoverContent = ({ canHideMore, columns, onColumnVisibilityChange }) => (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
        <Text tone="muted" variant="caption">
            {i18next.t('datagrid.columns_popover_title', { defaultValue: 'Show columns' })}
        </Text>
        <div className="tw-flex tw-flex-col tw-gap-2">
            {columns.map(column => {
                // Visible columns can only be hidden while at least
                // three "real" columns are showing. Hidden columns are
                // always re-enable-able, regardless of the floor.
                const disabled = column.visible && !canHideMore;
                return (
                    <Checkbox
                        checked={column.visible}
                        disabled={disabled}
                        id={`datagrid-col-${column.key}`}
                        key={column.key}
                        // `tw-inline-block` is required for the
                        // `first-letter` pseudo to apply — the spec
                        // only honours it on block-like display. The
                        // span sits inside Checkbox's inline `<label>`,
                        // so without this the capitalization silently
                        // no-ops.
                        label={<span className="tw-inline-block first-letter:tw-uppercase">{column.label}</span>}
                        onCheckedChange={checked => onColumnVisibilityChange(column.key, Boolean(checked))}
                    />
                );
            })}
        </div>
    </div>
);

const DataGridToolbar = ({
    actions,
    canHideMore,
    columns,
    onColumnVisibilityChange,
    selectedKeys,
    selectedRows
}) => {
    const [columnsOpen, setColumnsOpen] = React.useState(false);
    const hasActions = actions && actions.length > 0;
    const hasColumnsControl = columns && columns.length > 0 && typeof onColumnVisibilityChange === 'function';

    // The toolbar mounts only if there's something to show in it. An
    // unconditional render would paint an empty 8px-tall flex row above
    // tables that opt out of both actions and the columns control.
    if (!hasActions && !hasColumnsControl) {
        return null;
    }

    const selectedCount = selectedKeys.length;

    return (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            {hasActions ? actions.map(action => {
                const enabled = isActionEnabled(action, selectedCount);

                return (
                    <Button
                        appearance={action.appearance ?? 'outline'}
                        disabled={!enabled || action.disabled === true}
                        icon={action.icon}
                        key={action.label}
                        onClick={() => action.onClick?.(selectedKeys, selectedRows)}
                        size="small"
                        tone={action.tone ?? 'neutral'}
                    >
                        {action.label}
                    </Button>
                );
            }) : null}

            {selectedCount > 0 ? (
                <Text tone="muted" variant="caption">
                    {i18next.t('datagrid.selected_count', { count: selectedCount, defaultValue: '{{count}} selected' })}
                </Text>
            ) : null}

            {hasColumnsControl ? (
                // `ml-auto` shoves the Columns control to the right edge
                // even when there are no actions on the left, so the
                // position is stable across pages.
                <div className="tw-ml-auto">
                    <Popover
                        align="end"
                        content={(
                            <ColumnsPopoverContent
                                canHideMore={canHideMore}
                                columns={columns}
                                onColumnVisibilityChange={onColumnVisibilityChange}
                            />
                        )}
                        onOpenChange={setColumnsOpen}
                        open={columnsOpen}
                        trigger={(
                            <Button
                                appearance="outline"
                                icon="tableColumns"
                                size="small"
                                tone="neutral"
                            >
                                {i18next.t('datagrid.columns_button', { defaultValue: 'Columns' })}
                            </Button>
                        )}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default DataGridToolbar;
